import React, { useImperativeHandle, forwardRef } from 'react';

import { useElements, useStripe } from '@stripe/react-stripe-js';
import { StripeError } from '@stripe/stripe-js';
import { useTranslations } from 'next-intl';

import AddBillingAddressInformation from 'components/payment/components/AddBillingAddressInformation';
import StripeCheckoutForm from 'components/payment/components/StripeCheckoutForm';
import createStripePaymentIntent from 'components/payment/createStripePaymentIntent';
import usePaymentFormContext from 'hooks/usePaymentFormContext';
import { OptionalProductAdjustQuantityType } from 'types/productPackage';
import { normalizeProductQuantities } from 'utils/normalizeProductQuantities';

export interface StripePaymentRef {
  submitCardForm: () => Promise<void>; // Define the type of the method
}

const StripePayment = forwardRef(
  (
    {
      packageId,
      checkoutCompletedUrl,
      reference,
      fullName,
      email,
      slug,
      selectedOptionalProducts,
      handleSetErrorMessage,
      handleSetStripeFromState,
      handleSetPaymentLoading,
    }: {
      packageId: number;
      checkoutCompletedUrl: string;
      reference: string;
      fullName: string;
      email: string;
      slug: string;
      selectedOptionalProducts: OptionalProductAdjustQuantityType[];
      handleSetErrorMessage: (message: string) => void;
      handleSetStripeFromState: (stare: 'ready' | 'error' | 'loading') => void;
      handleSetPaymentLoading: (loading: boolean) => void;
    },
    ref,
  ) => {
    const stripe = useStripe();
    const elements = useElements();
    const { memberCompanyInfo, billingAddressData, shouldAddBillingAddress } =
      usePaymentFormContext();

    const tStripeAPIMessageCode = useTranslations('stripeAPIMessageCode');
    const tApiMessageCode = useTranslations('apiMessageCode');

    const handleError = (error: StripeError) => {
      const errorMessage = tStripeAPIMessageCode(`${error.code}`);

      handleSetErrorMessage(errorMessage);
      handleSetStripeFromState('error');
      handleSetPaymentLoading(false);
    };

    const submitCardForm = async () => {
      if (!stripe || !elements) {
        // Stripe.js hasn't yet loaded.
        return;
      }

      const { error: submitError } = await elements.submit();
      if (submitError) return;

      handleSetStripeFromState('ready');
      handleSetErrorMessage('');
      handleSetPaymentLoading(true);

      const payload = {
        packageId: packageId,
        userData: {
          name: fullName,
          email,
          fullName: fullName,
          reference,
          onCheckoutCompletedUrl: checkoutCompletedUrl,
          isCompany: memberCompanyInfo.isCompany,
          companyName: memberCompanyInfo.isCompany ? memberCompanyInfo.companyName : '',
          organizationNo: memberCompanyInfo.isCompany ? memberCompanyInfo.organizationNo : '',
        },
        isSaved: true,
        optionalProducts: normalizeProductQuantities(selectedOptionalProducts),
        ...(shouldAddBillingAddress && { billingAddress: billingAddressData }),
      };

      try {
        const clientSecretResponse = await createStripePaymentIntent(payload);

        if (clientSecretResponse?.messageCode) {
          throw new Error(clientSecretResponse.messageCode);
        }

        const { error } = await stripe.confirmPayment({
          elements,
          clientSecret: clientSecretResponse.clientSecret,
          confirmParams: {
            return_url: `${window.location.origin}/checkout/${
              slug
            }/auth/stripe-payment/complete?checkoutStateUuid=${encodeURIComponent(
              clientSecretResponse.checkoutStateUuid || '',
            )}`,
            payment_method_data: {
              billing_details: {
                email: email,
              },
            },
          },
        });

        if (error) {
          handleError(error);
          handleSetStripeFromState('error');
        } else {
          handleSetStripeFromState('ready');
          handleSetPaymentLoading(false);
        }
      } catch (error) {
        const errorMessage = tApiMessageCode('paymentFailed');
        handleSetErrorMessage(errorMessage);
        handleSetStripeFromState('error');
        handleSetPaymentLoading(false);
      }
    };

    useImperativeHandle(ref, () => ({
      submitCardForm,
    }));

    return (
      <>
        <StripeCheckoutForm />
        <AddBillingAddressInformation />
      </>
    );
  },
);

StripePayment.displayName = 'StripePayment';

export default StripePayment;
